import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { GoogleMap, useLoadScript, Autocomplete } from '@react-google-maps/api';
import Loader from 'react-loader-spinner';
import { fetchMarkers } from '../redux/actions/actionFetchMarkers';
import InfoWindowComponent from '../components/InfoWindowComponent';
import MarkerComponent from '../components/MarkerComponent';
import mapStyles from '../mapStyle';
import { Link } from 'react-router-dom'
import logo from '../images/logo.png'
import { motion, AnimatePresence } from 'framer-motion';

const mapContainerStyle = {
  width: "100vw",
  height: "100vh"
};

const center = {
  lat: 47.227638,
  lng: 2.213749
};

const options = {
  styles: mapStyles,
  disableDefaultUI: true,
  zoomControl: true
};

const Map = () => {
  const state = useSelector(state => state.markers);
  const dispatch = useDispatch();

  const [selected, setSelected] = useState(null);
  const [autocomplete, setAutocomplete] = useState(null);
  const handleLoad = (autoC) => {
    // Restrict results to France 🇫🇷
    autoC.setComponentRestrictions({ country: "fr" });
    setAutocomplete(autoC);
  };
  const [legendOpen, setLegendOpen] = useState(window.innerWidth >= 8000);
  useEffect(() => {
  const handleResize = () => {
        if (window.innerWidth < 8000) setLegendOpen(false);
        else setLegendOpen(true);
    };
window.addEventListener('resize', handleResize);
return () => window.removeEventListener('resize', handleResize);
}, []);

  const mapRef = useRef(null);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
    libraries: ['places'] 
  });

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  const handlePlaceChanged = () => {
  if (!autocomplete) return;

  const place = autocomplete.getPlace();
  if (!place.geometry) return;

  if (place.geometry.viewport) {
    mapRef.current.fitBounds(place.geometry.viewport);
  } else {
    mapRef.current.setCenter(place.geometry.location);
    mapRef.current.setZoom(12);
  }
  };

  const onPlaceChanged = () => {
    if (!autocomplete) return;

    const place = autocomplete.getPlace();
    if (!place.geometry) return;

    // If the place has a viewport (city/region)
    if (place.geometry.viewport) {
      mapRef.current.fitBounds(place.geometry.viewport);
    } else {
      // Otherwise center and zoom manually (e.g. for a single address)
      mapRef.current.setCenter(place.geometry.location);
      mapRef.current.setZoom(12);
    }
  };

  useEffect(() => {
    dispatch(fetchMarkers());
  }, [dispatch]);

  if (loadError) return "Erreur au chargement de la carte";
  if (!isLoaded) return "Carte en cours de chargement...";

  return (
    <div style={{ position: 'relative', display: 'flex', }}>
         <div id="block_container" className="header-container">
            <Link to="/"><img style={{ position: 'relative', display: 'flex', left: '10%', transform: 'translateX(-20%)', zIndex: 10 }} src={logo} alt="logo" /></Link>
                  {/* 🔍 Search bar */}
            <div style={{ position: 'relative', display: 'flex', top: '5%', left: '10%', transform: 'translateX(-5%)', zIndex: 10 }}>
                <Autocomplete
                onLoad={handleLoad} onPlaceChanged={handlePlaceChanged}
                >
                <input
                    type="text"
                    placeholder="Rechercher un département, ville, lieu, adresse..."
                    style={{
                    boxSizing: `border-box`,
                    border: `1px solid transparent`,
                    width: `300px`,
                    height: `40px`,
                    padding: `0 12px`,
                    borderRadius: `4px`,
                    boxShadow: `0 2px 6px rgba(0, 0, 0, 0.3)`,
                    fontSize: `16px`,
                    outline: `none`,
                    textOverflow: `ellipses`
                    }}
                />
                </Autocomplete>
            </div>

            <motion.div
                initial={false}
                animate={{
                    height: legendOpen ? 'auto' : '40px',
                    width: legendOpen ? '260px' : '120px',
                }}
                transition={{ type: 'spring', stiffness: 220, damping: 25 }}
                style={{
                    position: 'absolute',
                    top:'110%',
                    left: '5%',
                    backgroundColor: 'rgba(255, 255, 255, 0.75)',
                    borderRadius: '4px',
                    fontSize: '14px',
                    overflow: 'hidden',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                    cursor: 'pointer',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    userSelect: 'none',
                    zIndex: 25,
                }}
                onClick={() => setLegendOpen(!legendOpen)}
                >
                <div
                    style={{
                    padding: '8px 12px',
                    borderBottom: legendOpen ? '1px solid rgba(0,0,0,0.1)' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    }}
                >
                    <span
                    style={{
                        color: '#555',
                        fontFamily: '"Poppins", sans-serif',
                        fontWeight: 600,
                    }}
                    >
                    Légendes
                    </span>
                    <motion.span
                    animate={{ rotate: legendOpen ? 90 : 0 }}
                    transition={{ duration: 0.3 }}
                    style={{ fontWeight: 'bold', fontSize: '1rem', color: '#555', }}
                    >
                    ▶
                    </motion.span>
                </div>

                <AnimatePresence>
                    {legendOpen && (
                    <motion.div
                    key="legend-content"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.25 }}
                    style={{
                        padding: '10px',
                        fontFamily: '"Poppins", sans-serif',
                        color: '#555',
                        textAlign: 'left',
                    }}
                    >
                    {[
                        { src: '/google-pins/wa3_marker_aire_free.png', label: ' Libre' },
                        { src: '/google-pins/wa3_marker_aire_full.png', label: ' Complet' },
                        { src: '/google-pins/wa3_marker_aire_closed.png', label: ' Fermé' },
                        { src: '/google-pins/nonWa_marker.png', label: 'Infos à titre indicatif' },
                        { src: '/google-pins/wa_agp_marker.png', label: 'Grand-Passage' },
                    ].map(({ src, label }, index) => (
                        <div
                        key={index}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginBottom: '6px',
                        }}
                        >
                        <img
                            src={src}
                            alt={label}
                            style={{ height: '25px', width:'20px', opacity: 1 }}
                        />
                        <span style={{ fontSize: '0.9rem', lineHeight: '1.2rem' }}>{label}</span>
                        </div>
                    ))}
                    </motion.div>
                        )}
                    </AnimatePresence>
                </motion.div>
        </div>

      {/* 🗺️ Google Map */}
      <GoogleMap
        zoom={6}
        center={center}
        mapContainerStyle={mapContainerStyle}
        options={options}
        onLoad={onMapLoad}
      >
    <AnimatePresence>
        {state.isLoading ? (
         <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            style={{
              position: 'relative',
              top: '40%',
              left: '20%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '20px 40px',
              borderRadius: '15px',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
              pointerEvents: 'none', // allows interaction with map/autocomplete
              zIndex: 12,
              maxWidth: '50%',
              textAlign: 'center'
            }}
            >
            <Loader type="Bars" color="#FFF" height={80} width={80} />
            <p  style={{
                color: '#FFF',
                marginTop: '8px',
                fontSize: '1.2rem',
                fontFamily: '"Poppins", sans-serif',
                pointerEvents: 'none', 
            }}>Chargement des aires en cours...</p>
        </motion.div>
        ) : state.error !== '' ? (
          <p>{console.log(state.error)}</p>
        ) : (
          state.fetchMarkers.map(marker => (
            <MarkerComponent
              marker={marker}
              onClick={() => setSelected(marker)}
              key={marker.name}
            />
          ))
        )}
    </AnimatePresence>

        {selected ? (
          <InfoWindowComponent
            selected={selected}
            onCloseClick={() => setSelected(null)}
          />
        ) : null}
      </GoogleMap>

        
    </div>
    );
    };

export default Map;
